import React, {useEffect, useState} from "react"
import {Text, TextButton, TextInput} from "atmosfair-ui"
import './styles.css'
import {validateEmail, validateFirstAndLastName} from "../../forms/utils";
import {CompanyProps} from "../../../api/model/company";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {UserProps} from "../../../api/model/user";
import {useFusionAuth} from "@fusionauth/react-sdk";
import RegistrationWrapper from "../registration-wrapper/RegistrationWrapper";
import PhoneNumberForm, {PhoneNumberProps} from "../../forms/phone-number-form/PhoneNumberForm";
import AddressForm, {AddressProps} from "../../forms/address-form/AddressForm";
import {
    INITIAL_SELECTED_INTERNATIONAL_PHONE_PREFIX,
    VALIDATE_FORM_STARTING_NUMBER
} from "../../../constants/default-values";

export interface UserPropsForRegistration {
    phoneNumber: UserProps["phoneNumber"],
    internationalPhonePrefix: UserProps["internationalPhonePrefix"],
    registrationComplete: UserProps["registrationComplete"]
}

export interface CompanyPropsForRegistration extends Omit<CompanyProps, "id" | "name" | "members" | "agencies" | "isKlimaLink" | "isAdmin" | "adminIds"> {
}

interface CompleteUserAdminRegistrationProps {
    company: CompanyProps,
    user: UserProps,
    isCompanyAdmin: boolean,
    onRegister: (user: UserPropsForRegistration, company?: CompanyPropsForRegistration) => void
}

interface CompanyFormProps {
    companyEmail: CompanyProps["generalEmail"],
    companyFirstContactFirstName: CompanyProps["firstContactFirstName"],
    companyFirstContactLastName: CompanyProps["firstContactLastName"],
}

interface CompanyFormErrorProps {
    companyEmailError: boolean
    companyFirstContactFirstNameError: boolean
    companyFirstContactLastNameError: boolean
    // termsAccepted: boolean,
}

const CompleteUserAdminRegistration = ({
                                           company,
                                           user,
                                           isCompanyAdmin,
                                           onRegister,
                                       }: CompleteUserAdminRegistrationProps) => {
    const {firstName, lastName, email} = user
    // const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(false)
    // const [modalVisible, setModalVisible] = useState(false)
    const {name} = company
    const {logout} = useFusionAuth()
    const [validateForms, setValidateForms] = useState(VALIDATE_FORM_STARTING_NUMBER)
    const [companyAddressData, setCompanyAddressData] = useState<AddressProps | null>(null)
    const [userPhoneData, setUserPhoneData] = useState<PhoneNumberProps | null>(null)
    const [companyPhoneData, setCompanyPhoneData] = useState<PhoneNumberProps | null>(null)
    const [companyFormData, setCompanyFormData] = useState<CompanyFormProps>({
        companyEmail: '',
        companyFirstContactFirstName: '',
        companyFirstContactLastName: '',
    })
    const [companyFormErrorData, setCompanyFormErrorData] = useState<CompanyFormErrorProps>({
        companyEmailError: false,
        companyFirstContactFirstNameError: false,
        companyFirstContactLastNameError: false,
        // termsAccepted: false,
    })
    const {
        companyEmailError,
        companyFirstContactFirstNameError,
        companyFirstContactLastNameError,
        // termsAccepted
    } = companyFormErrorData
    const {
        companyEmail,
        companyFirstContactFirstName,
        companyFirstContactLastName
    } = companyFormData
    const {t} = useTranslation()

    useEffect(() => {
        if (isCompanyAdmin) {
            if (companyPhoneData && companyAddressData) _onCompleteRegistration()
        } else {
            if (userPhoneData) _onCompleteRegistration()
        }
    }, [companyAddressData, userPhoneData, companyPhoneData])

    const _updateCompanyFormData = <K extends keyof CompanyFormProps>(key: K, payload: CompanyFormProps[K]) => setCompanyFormData({
        ...companyFormData,
        [key]: payload
    })

    const _onCompleteRegistration = () => {
        if (userPhoneData) {
            const {phoneNumber, internationalPhonePrefix} = userPhoneData
            const userObject = {
                phoneNumber,
                internationalPhonePrefix: internationalPhonePrefix === null || typeof internationalPhonePrefix === 'undefined' ? INITIAL_SELECTED_INTERNATIONAL_PHONE_PREFIX : internationalPhonePrefix,
                registrationComplete: false,
            }
            if (isCompanyAdmin && companyAddressData && companyPhoneData) {
                const {companyEmail, companyFirstContactFirstName, companyFirstContactLastName} = companyFormData
                const {street, houseNumber, zipCode, city, country, additionalAddressInformation} = companyAddressData
                const {phoneNumber, internationalPhonePrefix} = companyPhoneData
                const companyObject: CompanyPropsForRegistration = {
                    generalEmail: companyEmail,
                    firstContactFirstName: companyFirstContactFirstName,
                    firstContactLastName: companyFirstContactLastName,
                    address: {
                        street,
                        houseNumber,
                        zipCode,
                        city,
                        country: country!!,
                        additionalInformation: additionalAddressInformation,
                        phoneNumber,
                        internationalPhonePrefix: internationalPhonePrefix!!
                    }
                }
                onRegister(userObject, companyObject)
            } else {
                onRegister({...userObject, registrationComplete: true})
            }
        }
    }

    const _validateCompanyForm = () => {
        const validFirstContactFirstName = validateFirstAndLastName(companyFirstContactFirstName)
        const validFirstContactLastName = validateFirstAndLastName(companyFirstContactLastName)
        const validEmail = validateEmail(companyEmail)
        setCompanyFormErrorData({
            companyEmailError: !validEmail,
            companyFirstContactFirstNameError: !validFirstContactFirstName,
            companyFirstContactLastNameError: !validFirstContactLastName,
            // termsAccepted: !termsAndConditionsAccepted
        })
        return [
            validFirstContactFirstName,
            validFirstContactLastName,
            validEmail,
            // termsAndConditionsAccepted,
        ].every(Boolean)
    }

    const _setValidateForms = () => {
        if (isCompanyAdmin && _validateCompanyForm()) {
            setValidateForms(validateForms + 1)
            return
        }
        if (!isCompanyAdmin) setValidateForms(validateForms + 1)
    }

    return <RegistrationWrapper title={"SCREENS.REGISTRATION.COMPANY_USER_TITLE"}>
        <div
            onKeyDown={event => {
                if (event.key === "Enter") {
                    event.preventDefault()
                    _setValidateForms()
                }
            }}
            className={clsx(
                isCompanyAdmin && "complete-admin-user-registration-spacer",
                "complete-user-admin-registration-missing-information-wrapper")}>
            <Text
                classes={"complete-user-admin-registration-company-title"}
                variant={"headlineMedium"}>
                {t("SCREENS.REGISTRATION.MISSING_USER_INFORMATION_TITLE", {
                    userName: `${firstName} ${lastName}`,
                    email: email
                })}
            </Text>
            <div className={'complete-user-admin-registration-missing-user-information-wrapper'}>
                <PhoneNumberForm
                    showInternationalPhonePrefix={true}
                    internationalPhonePrefixRequired={true}
                    phoneNumberRequired={true}
                    validateForm={validateForms}
                    onFormIsValid={setUserPhoneData}/>
            </div>
        </div>
        {isCompanyAdmin && <div className={"complete-user-admin-registration-missing-information-wrapper"}>
            <Text
                variant={"headlineMedium"}
                classes={'complete-user-admin-registration-company-title'}>
                {t("SCREENS.REGISTRATION.COMPANY_ADMIN.MISSING_COMPANY_INFORMATION_TITLE", {company: name})}
            </Text>
            <div className={"complete-user-admin-registration-row-element"}>
                <TextInput
                    aria-errormessage={"LABELS.COMMON.E_MAIL"}
                    error={companyEmailError}
                    onBlur={event => _updateCompanyFormData('companyEmail', event.target.value.trim())}
                    onChange={event => _updateCompanyFormData('companyEmail', event.target.value)}
                    containerStyle={{flex: 1}}
                    required={true}
                    placeholder={"GENERAL.EMAIL"}
                    label={"GENERAL.COMPANY_EMAIL"}
                    value={companyEmail}/>
                <TextInput
                    aria-errormessage={"ERRORS.SETTINGS.FIRST_CONTACT_PERSON_FIRST_NAME"}
                    error={companyFirstContactFirstNameError}
                    value={companyFirstContactFirstName}
                    onBlur={event => _updateCompanyFormData('companyFirstContactFirstName', event.target.value.trim())}
                    onChange={event => _updateCompanyFormData('companyFirstContactFirstName', event.target.value)}
                    containerStyle={{flex: 1}}
                    required={true}
                    placeholder={"GENERAL.FIRST_NAME"}
                    label={"GENERAL.FIRST_CONTACT_PERSON_FIRST_NAME"}/>
                <TextInput
                    required={true}
                    aria-errormessage={"ERRORS.SETTINGS.FIRST_CONTACT_PERSON_LAST_NAME"}
                    error={companyFirstContactLastNameError}
                    value={companyFirstContactLastName}
                    onBlur={event => _updateCompanyFormData('companyFirstContactLastName', event.target.value.trim())}
                    onChange={event => _updateCompanyFormData('companyFirstContactLastName', event.target.value)}
                    containerStyle={{flex: 1}}
                    placeholder={"GENERAL.FIRST_NAME"}
                    label={"GENERAL.FIRST_CONTACT_PERSON_LAST_NAME"}/>
            </div>
            <div className={"complete-user-admin-registration-row-element"}>
                <AddressForm
                    showCountry={true}
                    countryRequired={true}
                    streetRequired={true}
                    houseNumberRequired={true}
                    zipCodeRequired={true}
                    cityRequired={true}
                    additionalAddressInformationRequired={false}
                    validateForm={validateForms}
                    onFormIsValid={setCompanyAddressData}/>
            </div>
            <PhoneNumberForm
                showInternationalPhonePrefix={true}
                internationalPhonePrefixRequired={true}
                phoneNumberRequired={true}
                validateForm={validateForms}
                onFormIsValid={setCompanyPhoneData}/>
            {/*<div className={"complete-user-admin-registration-terms-and-conditions-accept-and-pdf-checkbox-wrapper"}>*/}
            {/*    <CheckboxInput*/}
            {/*        aria-errormessage={"ERRORS.COMMON.ACCEPT_TERMS"}*/}
            {/*        error={termsAccepted}*/}
            {/*        containerStyle={{width: "unset"}}*/}
            {/*        hideLabel={true}*/}
            {/*        label={'LABELS.SETTINGS.TAC_CHECKBOX'}*/}
            {/*        text={"LABELS.SETTINGS.TAC_CHECKBOX"}*/}
            {/*        checked={termsAndConditionsAccepted}*/}
            {/*        onChange={() => setTermsAndConditionsAccepted(!termsAndConditionsAccepted)}/>*/}
            {/*    <Text onClick={() => setModalVisible(true)} variant={"link"}>*/}
            {/*        GENERAL.SHOW_TERMS*/}
            {/*    </Text>*/}
            {/*</div>*/}
        </div>}
        <div className={"complete-admin-user-registration-language-selector-button-wrapper"}>
            <TextButton variant={"abort"} onClick={() => logout()}>GENERAL.ABORT</TextButton>
            <TextButton
                containerClasses={"complete-user-admin-registration-proceed-button"}
                onClick={() => _setValidateForms()}>
                GENERAL.PROCEED
            </TextButton>
        </div>
        {/*<Modal*/}
        {/*    onClose={() => setModalVisible(false)}*/}
        {/*    paperClasses={"complete-user-admin-registration-terms-and-conditions-accept-and-pdf-modal"}*/}
        {/*    showPaper={true}*/}
        {/*    headline={"GENERAL.TERMS"}*/}
        {/*    show={modalVisible} onBackdropClick={() => setModalVisible(false)}>*/}
        {/*    <div className={'complete-user-admin-registration-terms-and-conditions-accept-and-pdf-iframe'}>*/}
        {/*        <iframe*/}
        {/*            width="100%"*/}
        {/*            height="100%"*/}
        {/*            title={t("GENERAL.TERMS")}*/}
        {/*            src={"https://b2b.dev.klimalink.org/terms_and_conditions.pdf"}/>*/}
        {/*    </div>*/}
        {/*</Modal>*/}
    </RegistrationWrapper>
}

export default CompleteUserAdminRegistration